import * as NextSkyTheme from "@NextSkyTheme/global";

class BoughtTogetherCard extends HTMLElement {
  connectedCallback() {
    this.kit = this.closest("bought-together-kit");
    this.select = this.querySelector("[data-btk-variant]");
    this.variantText = this.querySelector("[data-btk-variant-text]");
    this.priceEl = this.querySelector("[data-btk-price]");
    this.checkbox = this.querySelector(".bought-together-checkbox");
    this.isMain = this.dataset.isMain === "true";

    this.onVariantChange = this.handleVariantChange.bind(this);
    this.onCheckChange = this.handleCheckChange.bind(this);

    this.select?.addEventListener("change", this.onVariantChange);
    this.checkbox?.addEventListener("change", this.onCheckChange);

    this.syncSelectedState();
    this.syncVariantLabel();
  }

  disconnectedCallback() {
    this.select?.removeEventListener("change", this.onVariantChange);
    this.checkbox?.removeEventListener("change", this.onCheckChange);
  }

  handleVariantChange() {
    this.syncVariantLabel();
    this.syncPriceDisplay();
    this.syncCheckboxPrice();
    if (this.select) {
      const option = this.select.options[this.select.selectedIndex];
      if (option) {
        this.dataset.price = option.dataset.price || this.dataset.price;
        this.dataset.comparePrice =
          option.dataset.priceCompare ||
          option.dataset.price ||
          this.dataset.comparePrice;
      }
    }
    this.kit?.recalculate();
  }

  handleCheckChange() {
    this.syncSelectedState();
    this.kit?.recalculate();
  }

  syncSelectedState() {
    if (this.isMain) return;
    this.classList.toggle("is-selected", Boolean(this.checkbox?.checked));
  }

  syncVariantLabel() {
    if (!this.select || !this.variantText) return;
    const option = this.select.options[this.select.selectedIndex];
    const size = option?.dataset.size || option?.textContent?.trim() || "";
    const colorSpan = this.variantText.querySelector(".btk-card__variant-color");
    const colorHtml = colorSpan ? colorSpan.outerHTML : "";
    this.variantText.innerHTML = `Tamanho: ${size}${colorHtml}`;
  }

  syncPriceDisplay() {
    if (!this.select || !this.priceEl) return;
    const option = this.select.options[this.select.selectedIndex];
    const price = Number(option?.dataset.price || 0);
    if (!price || !window.cartStrings?.money_format) return;
    this.priceEl.innerHTML = NextSkyTheme.formatMoney(
      price,
      cartStrings.money_format
    );
  }

  syncCheckboxPrice() {
    if (!this.select || !this.checkbox) return;
    const option = this.select.options[this.select.selectedIndex];
    if (!option) return;
    this.checkbox.dataset.price = option.dataset.price || "0";
    this.checkbox.dataset.priceCompare =
      option.dataset.priceCompare || option.dataset.price || "0";
  }

  getSelectedPrice() {
    if (this.select) {
      const option = this.select.options[this.select.selectedIndex];
      return Number(option?.dataset.price || this.dataset.price || 0);
    }
    if (this.checkbox) {
      return Number(this.checkbox.dataset.price || this.dataset.price || 0);
    }
    return Number(this.dataset.price || 0);
  }

  getSelectedComparePrice() {
    if (this.select) {
      const option = this.select.options[this.select.selectedIndex];
      return Number(
        option?.dataset.priceCompare ||
          option?.dataset.price ||
          this.dataset.comparePrice ||
          0
      );
    }
    if (this.checkbox) {
      return Number(
        this.checkbox.dataset.priceCompare ||
          this.checkbox.dataset.price ||
          this.dataset.comparePrice ||
          0
      );
    }
    return Number(this.dataset.comparePrice || this.dataset.price || 0);
  }

  getSelectedVariantId() {
    return this.select?.value || null;
  }

  isIncluded() {
    return this.isMain || Boolean(this.checkbox?.checked);
  }
}

class BoughtTogetherKit extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector(".bought-together-products-form form");
    this.recalculate();

    // Keep totals in sync if legacy CardBoughtTogether fires.
    this.addEventListener("change", (event) => {
      if (
        event.target.matches(".bought-together-checkbox, [data-btk-variant]")
      ) {
        this.recalculate();
      }
    });
  }

  recalculate() {
    const cards = Array.from(this.querySelectorAll("bought-together-card"));
    let total = 0;
    let compare = 0;

    cards.forEach((card) => {
      const productId = card.dataset.productId;
      const variantSlot = this.querySelector(
        `.variant-select[product-id="${productId}"]`
      );
      if (!variantSlot) return;

      const inputs = variantSlot.querySelectorAll("input");
      const included = card.isIncluded();
      const variantId = card.getSelectedVariantId();

      if (included) {
        total += card.getSelectedPrice();
        compare += card.getSelectedComparePrice() || card.getSelectedPrice();
        if (variantId) {
          const idInput = variantSlot.querySelector('input[name="items[][id]"]');
          if (idInput) idInput.value = variantId;
        }
        inputs.forEach((input) => {
          input.disabled = false;
        });
      } else {
        inputs.forEach((input) => {
          input.disabled = true;
        });
      }
    });

    const mainCard = this.querySelector("bought-together-card[data-is-main='true']");
    if (mainCard) {
      this.dataset.price = String(mainCard.getSelectedPrice());
      this.dataset.priceCompare = String(
        mainCard.getSelectedComparePrice() || mainCard.getSelectedPrice()
      );
    }

    const discount = Number(this.dataset.discountPercent || 0);
    if (discount > 0) {
      compare = total;
      total = Math.round(total - (total * discount) / 100);
    }

    this.renderTotal(total, compare);
    this.renderInstallments(total);

    const submit = this.querySelector(".btk__submit");
    if (submit) {
      submit.disabled = total <= 0;
    }
  }

  renderTotal(total, compare) {
    const priceEl = this.querySelector(".btk__total-price");
    if (priceEl && window.cartStrings?.money_format) {
      priceEl.innerHTML = NextSkyTheme.formatMoney(
        total,
        cartStrings.money_format
      );
    }

    const compareEl = this.querySelector(".btk__compare-price");
    if (compareEl && window.cartStrings?.money_format) {
      if (compare > total) {
        compareEl.classList.remove("hidden");
        compareEl.innerHTML = NextSkyTheme.formatMoney(
          compare,
          cartStrings.money_format
        );
      } else {
        compareEl.classList.add("hidden");
      }
    }
  }

  renderInstallments(total) {
    const count = Number(this.dataset.installments || 10) || 10;
    const valueEl = this.querySelector("[data-btk-installment-value]");
    if (!valueEl || !window.cartStrings?.money_format) return;
    const installment = Math.floor(total / count);
    valueEl.innerHTML = NextSkyTheme.formatMoney(
      installment,
      cartStrings.money_format
    );
  }
}

if (!customElements.get("bought-together-card")) {
  customElements.define("bought-together-card", BoughtTogetherCard);
}

if (!customElements.get("bought-together-kit")) {
  customElements.define("bought-together-kit", BoughtTogetherKit);
}
